import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import BigNumber from 'bignumber.js'
import styled from 'styled-components'
import { useWeb3React } from '@web3-react/core'
import { Text, Flex, Button, HelpIcon, useModal, useMatchBreakpoints, RowType } from 'toonswap-uikit'
import { Doughnut } from 'react-chartjs-2'
import orderBy from 'lodash/orderBy'
import partition from 'lodash/partition'
import { useTranslation } from 'contexts/Localization'
import { usePools, usePriceBowUsd, useOneDayVolume } from 'state/hooks'
import { Pool } from 'state/types'
import useRefresh from 'hooks/useRefresh'
import { useXbowApprove } from 'hooks/useApprove'
import { useXbowAllowance } from 'hooks/useAllowance'
import useBowInfo, { useUserInfo, useXbowStake, useXbowUnstake } from 'hooks/useBowInfo'
import { getBalanceNumber } from 'utils/formatBalance'
import FlexLayout from 'components/layout/Flex'
import UnlockButton from 'components/UnlockButton'
import PoolCard from './components/PoolCard'
import PoolTabButtons from './components/PoolTabButtons'
import SearchInput from './components/SearchInput'
import Select, { OptionProps } from './components/Select/Select'
import PoolTable from './components/PoolTable/PoolTable'
import { RowProps } from './components/PoolTable/Row'
import { DesktopColumnSchema } from './components/PoolTable/types'
import StakeModal from './components/xbow/StakeModal'
import UnstakeModal from './components/xbow/UnstakeModal'

const PoolCardLayout = styled(FlexLayout)`
  justify-content: flex-start;
  gap: 0 24px;

  & > * {
    max-width: 360px;
    margin: 0 0 32px;
  }

  @media (max-width: 768px) {
    & > * {
      width: 100%;
      min-width: 0;
      margin-left: 0;
      margin-right: 0;
    }
  }
`

enum ViewMode {
  TABLE = 'TABLE',
  CARD = 'CARD',
}

const getPoolSymbol = (pool: Pool) => {
  return [].includes(pool.sousId) ? pool.stakingToken.symbol : pool.earningToken.symbol
}

const getPoolLabel = (pool: Pool) => {
  const symbol = getPoolSymbol(pool)
  if ([8, 9].includes(pool.sousId)) {
    return `${pool.stakingToken.symbol}/${pool.earningToken.symbol}`
  }
  return symbol
}

const ControlContainer = styled.div`
  display: flex;
  width: 100%;
  position: relative;
  justify-content: space-between;
  flex-direction: column;
  gap: 16px;
  ${({ theme }) => theme.mediaQueries.lg} {
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
  }
`

const FilterLabelWrapper = styled.div`
  width: 100%;
  min-width: 0;

  > ${Text} {
    font-size: 12px;
  }

  ${({ theme }) => theme.mediaQueries.sm} {
    width: auto;
  }
`

const SearchFilterLabelWrapper = styled(FilterLabelWrapper)`
  margin-left: 0;

  ${({ theme }) => theme.mediaQueries.sm} {
    margin-left: 16px;
  }
`

const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  gap: 12px;

  ${({ theme }) => theme.mediaQueries.sm} {
    flex-direction: row;
    align-items: center;
    gap: 0;
    width: auto;
    padding: 0;
  }
`

const ViewControls = styled.div`
  flex-wrap: wrap;
  justify-content: space-between;
  display: flex;
  align-items: center;
  width: 100%;
  > div {
  }
  ${({ theme }) => theme.mediaQueries.sm} {
    justify-content: flex-start;
    width: auto;
    > div {
      padding: 0;
    }
  }
`

const Farm: React.FC = () => {
  const { pathname } = useLocation()
  const { isXl } = useMatchBreakpoints()
  const isMobile = !isXl
  const { t } = useTranslation()
  const { account } = useWeb3React()
  const pools = usePools(account)

  const [update, setUpdate] = useState(false)
  const [timestamp, setTimestamp] = useState(0)
  const [viewMode, setViewMode] = useState(ViewMode.CARD)
  const [query, setQuery] = useState('')
  const [sortOption, setSortOption] = useState('hot')
  const { fastRefresh } = useRefresh()

  const onApprove = useXbowApprove()
  const allowance = useXbowAllowance(update)
  const { totalLockedBow, xbowRatio, delayToWithdraw } = useBowInfo(update)
  const bowPrice = usePriceBowUsd()
  const { bowBalance, xbowBalance, claimableAmount, stakedTime } = useUserInfo(update)
  const { onStake: stakeXToon } = useXbowStake()
  const { onUnstake: unstakeXToon } = useXbowUnstake()
  // Re-read the balances as soon as a stake or unstake goes through
  const onStake = useCallback(
    async (amount: string) => {
      await stakeXToon(amount)
      setUpdate((value) => !value)
    },
    [stakeXToon],
  )
  const onUnstake = useCallback(
    async (amount: string) => {
      await unstakeXToon(amount)
      setUpdate((value) => !value)
    },
    [unstakeXToon],
  )
  const oneDayVolume = useOneDayVolume()

  useEffect(() => {
    setTimestamp(Math.floor(new Date().getTime() / 1000))
  }, [fastRefresh])

  useEffect(() => {
    // Step 2 pools are cards on every screen size, like the original two-step page
    setViewMode(ViewMode.CARD)
  }, [pathname, isMobile])

  const [finishedPools, openPools] = useMemo(
    () => partition(pools, (pool) => pool.isFinished || timestamp > pool.endTime),
    [timestamp, pools],
  )
  // Staked only: show all pools (live + finished) where user has staked balance
  const stackedOnlyPools = useMemo(
    () => pools.filter((pool) => pool.userData && new BigNumber(pool.userData.stakedBalance).isGreaterThan(0)),
    [pools],
  )

  const poolsToDisplay = useMemo(() => {
    if (pathname.includes('staked')) return orderBy(stackedOnlyPools, ['sortOrder'])
    if (pathname.includes('history')) {
      return orderBy(finishedPools, ['sortOrder']).map((item) => ({
        ...item,
        isFinished: item.isFinished || timestamp > item.endTime,
      }))
    }
    return orderBy(openPools, ['sortOrder'])
  }, [pathname, openPools, stackedOnlyPools, finishedPools, timestamp])

  const filteredAndSortedPools = useMemo(() => {
    let list = poolsToDisplay
    if (query) {
      const lowercaseQuery = query.toLowerCase()
      list = list.filter((pool) => {
        const label = getPoolLabel(pool).toLowerCase()
        const stakingSymbol = pool.stakingToken.symbol.toLowerCase()
        const earningSymbol = pool.earningToken.symbol.toLowerCase()
        return (
          label.includes(lowercaseQuery) ||
          stakingSymbol.includes(lowercaseQuery) ||
          earningSymbol.includes(lowercaseQuery)
        )
      })
    }
    switch (sortOption) {
      case 'earned':
        return orderBy(
          list,
          (pool) => (pool.userData?.pendingReward ? new BigNumber(pool.userData.pendingReward).toNumber() : 0),
          'desc',
        )
      case 'staked':
        return orderBy(
          list,
          (pool) => (pool.userData?.stakedBalance ? new BigNumber(pool.userData.stakedBalance).toNumber() : 0),
          'desc',
        )
      case 'hot':
      default:
        return orderBy(list, ['sortOrder'])
    }
  }, [poolsToDisplay, query, sortOption])

  const rowData: RowProps[] = useMemo(
    () =>
      filteredAndSortedPools.map((pool) => {
        const stakedBalance = new BigNumber(pool.userData?.stakedBalance || 0)
        const earnings = getBalanceNumber(new BigNumber(pool.userData?.pendingReward || 0), pool.earningToken.decimals)
        return {
          pool: {
            label: `${t('Earn')} ${pool.earningToken.symbol}`,
            sousId: pool.sousId,
            earningSymbol: pool.earningToken.symbol,
            stakingSymbol: pool.stakingToken.symbol,
            isFinished: pool.isFinished,
          },
          earned: { earnings, sousId: pool.sousId },
          apr: pool, // required by useTable: every column name must exist on row (APR is computed in PoolApr from pool)
          staked: {
            value: getBalanceNumber(stakedBalance, pool.stakingToken.decimals),
            decimals: pool.stakingToken.decimals,
          },
          endTime: {
            endTime: pool.endTime,
          },
          details: pool,
        }
      }),
    [filteredAndSortedPools, t],
  )

  const poolTableColumns = useMemo(
    () =>
      DesktopColumnSchema.map((column) => ({
        id: column.id,
        name: column.name,
        label: column.label,
        sort: (a: RowType<RowProps>, b: RowType<RowProps>) => {
          switch (column.name) {
            case 'pool':
              return a.original.pool.sousId - b.original.pool.sousId
            case 'earned':
              return (a.original.earned.earnings ?? 0) - (b.original.earned.earnings ?? 0)
            case 'staked':
              return a.original.staked.value - b.original.staked.value
            case 'endTime':
              return a.original.endTime.endTime - b.original.endTime.endTime
            default:
              return 0
          }
        },
        sortable: column.sortable,
      })),
    [],
  )

  const renderPoolContent = () => {
    if (viewMode === ViewMode.TABLE) {
      return <PoolTable data={rowData} columns={poolTableColumns} />
    }
    return (
      <PoolCardLayout>
        {filteredAndSortedPools.map((pool) => (
          <PoolCard key={pool.sousId} pool={pool} />
        ))}
      </PoolCardLayout>
    )
  }

  const handleChangeQuery = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value)
  }

  const handleSortOptionChange = (option: OptionProps): void => {
    setSortOption(option.value)
  }

  const [onPresentStake] = useModal(<StakeModal max={bowBalance} onConfirm={onStake} />)
  const [onPresentUnstake] = useModal(
    <UnstakeModal max={xbowBalance} onConfirm={onUnstake} deadline={stakedTime + delayToWithdraw} />,
  )

  const data = {
    datasets: [
      {
        data: [xbowRatio.toNumber(), 1],
        // The xTOON share of the ring uses the orange-to-yellow gradient from the design
        backgroundColor: (context) => {
          if (context.dataIndex !== 0) return 'rgba(51, 51, 51, 0.1)'
          const { ctx, width } = context.chart
          const gradient = ctx.createLinearGradient(width, 0, 0, 0)
          gradient.addColorStop(0, '#fe7b2a')
          gradient.addColorStop(1, '#f7ce47')
          return gradient
        },
      },
    ],
  }

  const options = {
    responsive: false,
    maintainAspectRatio: false,
    legend: {
      display: false,
    },
    tooltips: {
      enabled: false,
    },
    elements: {
      arc: {
        borderWidth: 0,
      },
    },
    cutoutPercentage: 90,
  }

  const renderConnectedBtn = () => {
    return allowance.gt(0) ? (
      <>
        <StyledButton onClick={onPresentStake}>
          <img src="/images/assets/xbow/plus.svg" alt="plus" />
          {t('Stake')}
        </StyledButton>
        <UnstakeButton onClick={onPresentUnstake}>
          <img src="/images/assets/xbow/minus.svg" alt="minus" />
          {t('Unstake')}
        </UnstakeButton>
      </>
    ) : (
      <StyledButton
        onClick={async () => {
          await onApprove()
          setUpdate(!update)
        }}
      >
        {t('Approve')}
      </StyledButton>
    )
  }

  const showStep1 = true
  const apr = totalLockedBow.isZero()
    ? 0
    : new BigNumber(oneDayVolume.oneDayVolumeUSD)
        .times(0.001)
        .times(365)
        .div(totalLockedBow.times(bowPrice))
        .times(100)
        .toFormat(2)

  return (
    <PageWrap>
      <Banner src="/images/staking/banner.jpg" alt={t('xTOON: Stake your TOONs here to earn more!')} />
      {showStep1 && (
        <>
          <StepTitle>
            <span>{t('Step 1:')}</span> {t('Stake TOON, Receive xTOON Immediately')}
          </StepTitle>
          <XbowCallout>
            <StakingCard>
              <CardImage src="/images/staking/step1.jpg" alt="" />
              <CardBody>
                <BodyText>
                  {t(
                    'Stake TOON here and receive xTOON as receipt representing your share of the pool. This pool automatically compounds by using a portion of all trade fees to buy back TOON which means the xTOON to TOON ratio will grow over time!',
                  )}
                </BodyText>
                <FeeNote>
                  <InfoIcon aria-hidden="true">i</InfoIcon>
                  <BodyText>
                    {t(
                      'There is a 4% withdraw fee if $xTOON is withdrawn to TOON within 3 days. 2% is burned and 2% is returned to the $xTOON contract.',
                    )}
                  </BodyText>
                </FeeNote>
              </CardBody>
            </StakingCard>
            <StakingCard>
              <RatioSection>
                <RatioTitle>1 xTOON</RatioTitle>
                <CircleWrapper>
                  <Doughnut data={data} options={options} width={140} height={140} />
                  <LabelWrapper>
                    <RatioValue>={xbowRatio.toFormat(4)}</RatioValue>
                    <RatioUnit>TOON</RatioUnit>
                  </LabelWrapper>
                </CircleWrapper>
              </RatioSection>
              <InfoSection>
                <InfoRow>
                  <InfoLabel>{t('Claimable TOON')}</InfoLabel>
                  <InfoValue>${claimableAmount.times(bowPrice).toFormat(2)}</InfoValue>
                </InfoRow>
                <AmountWrapper>
                  <img src="/images/pools/toon.png" alt="TOON" />
                  <InfoLabel>{claimableAmount.toFormat(2)}</InfoLabel>
                </AmountWrapper>
                <InfoRow>
                  <InfoLabel>TVL</InfoLabel>
                  <InfoValue>${totalLockedBow.times(bowPrice).toFormat(2)}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <Flex alignItems="center">
                    <InfoLabel>{t('Approximately APR')}</InfoLabel>
                    <HelpWrapper title={t('Estimated yearly return, based on the last 24 hours of trading volume')}>
                      <HelpIcon color="#fde272" width="14px" />
                    </HelpWrapper>
                  </Flex>
                  <InfoValue>{apr}%</InfoValue>
                </InfoRow>
              </InfoSection>
              <ButtonSection>{account ? renderConnectedBtn() : <UnlockButton />}</ButtonSection>
            </StakingCard>
          </XbowCallout>
        </>
      )}
      <StepTitle>
        <span>{t('Step 2:')}</span> {t('Stake xTOON, Earn Tokens of Your Choice Over Time')}
      </StepTitle>
      <ControlContainer>
        <ViewControls>
          <PoolTabButtons />
        </ViewControls>
        <FilterContainer>
          <FilterLabelWrapper>
            <Select
              fullWidthOnMobile
              prefixLabel={t('Sort by')}
              options={[
                { label: t('Hot'), value: 'hot' },
                { label: t('Earned'), value: 'earned' },
                { label: t('Your Staked'), value: 'staked' },
              ]}
              onChange={handleSortOptionChange}
            />
          </FilterLabelWrapper>
          <SearchFilterLabelWrapper>
            <SearchInput onChange={handleChangeQuery} value={query} />
          </SearchFilterLabelWrapper>
        </FilterContainer>
      </ControlContainer>
      <div style={{ marginTop: 32 }}>{renderPoolContent()}</div>
    </PageWrap>
  )
}
const PageWrap = styled.div`
  width: 100%;
  max-width: 1130px;
  margin: 20px auto 80px;
  display: flex;
  flex-direction: column;
  align-items: stretch;

  @media (max-width: 768px) {
    margin: 12px auto 80px;
  }
`

const Banner = styled.img`
  display: block;
  width: 100%;
  height: auto;
  border-radius: 24px;
`

// const Hero = styled.div`
//   align-items: center;
//   color: ${({ theme }) => theme.colors.primary};
//   display: grid;
//   grid-gap: 32px;
//   grid-template-columns: 1fr;
//   margin-left: auto;
//   margin-right: auto;
//   max-width: 250px;
//   padding: 48px 0;
//   ul {
//     margin: 0;
//     padding: 0;
//     list-style-type: none;
//     font-size: 16px;
//     li {
//       margin-bottom: 4px;
//     }
//   }
//   img {
//     height: auto;
//     max-width: 100%;
//   }
//   @media (min-width: 576px) {
//     grid-template-columns: 1fr 1fr;
//     margin: 0;
//     max-width: none;
//   }
// `

const StepTitle = styled.h3`
  margin: 40px 0 32px;
  text-align: center;
  font-size: 24px;
  font-weight: 600;
  line-height: 28px;
  color: #1c1917;

  span {
    color: #fe7b2a;
  }

  @media (max-width: 768px) {
    margin: 28px 0 20px;
    font-size: 20px;
  }
`

const XbowCallout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  ${({ theme }) => theme.mediaQueries.sm} {
    grid-template-columns: 1fr 1fr;
  }
`

const StakingCard = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #475355;
  border: 3px solid #1d2425;
  border-radius: 25px;
`

const CardImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
`

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px;
`

const BodyText = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  color: #ffffff;
`

const FeeNote = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 9px 16px;
  background: #374244;
  border-radius: 18px;
`

const InfoIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #fe7b2a;
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  line-height: 1;
`

const RatioSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 240px;
  padding-top: 30px;
  background: #fefbe8 url('/images/assets/xbow/pattern.svg') center / cover no-repeat;
`

const RatioTitle = styled.div`
  margin-bottom: 16px;
  font-size: 22px;
  font-weight: 600;
  line-height: 24px;
  color: #0a0a0a;
`

const CircleWrapper = styled.div`
  position: relative;
  width: 140px;
  height: 140px;
`

const LabelWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`

const RatioValue = styled.div`
  font-size: 24px;
  font-weight: 600;
  line-height: 1.05;
  color: #1c1917;
`

const RatioUnit = styled.div`
  margin-top: 4px;
  font-size: 14px;
  line-height: 22px;
  color: #a9a29d;
`

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 24px 24px 0;
`

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const InfoLabel = styled.span`
  font-size: 16px;
  line-height: 24px;
  color: rgba(255, 255, 255, 0.65);
`

const InfoValue = styled.span`
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #ffffff;
  text-align: right;
`

const AmountWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
  margin-top: -8px;

  img {
    width: 18px;
    height: 18px;
  }
`

const HelpWrapper = styled.span`
  display: flex;
  margin-left: 6px;
  cursor: help;
`

const ButtonSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: auto;
  padding: 24px;

  button {
    width: 100%;
    height: 52px;
    border: 3px solid #2b3335 !important;
    border-radius: 12px !important;
    font-size: 18px !important;
    font-weight: 700 !important;
  }
`

const StyledButton = styled(Button)`
  background: #fe7b2a;
  color: #ffffff;

  img {
    margin-right: 12.5px;
  }
`

const UnstakeButton = styled(StyledButton)`
  background: linear-gradient(265.22deg, #ed952e 0%, #f7ce47 100%);
`

export default Farm
